/**
 * CLI Knobs Management
 *
 * Allows superadmins to manage knobs (global config) via the CLI.
 */

import process from "node:process";
import { resolveConfig, getArgValue, hasFlag } from "./auth.js";
import {
  type LifecycleStage,
  toLifecycleLabel,
  parseLifecycleStage,
  KNOB_LIFECYCLE_STAGES,
  apiRequest,
  fetchList,
} from "./cli-helpers.js";

type SerializedKnob = {
  key: string;
  description: string | null;
  valueType: "boolean" | "string" | "number" | "json";
  value: unknown;
  lifecycleStage: LifecycleStage;
  finalValue: unknown;
  updatedAt: string | null;
  updatedBy: string | null;
  createdAt: string | null;
  isStale: boolean;
};

type KnobApiResponse = {
  ok: boolean;
  error?: string;
  knob?: SerializedKnob;
};

function formatValue(valueType: SerializedKnob["valueType"], value: unknown): string {
  if (valueType === "json") return JSON.stringify(value);
  return String(value);
}

function formatFinalValue(knob: SerializedKnob): string {
  if (knob.lifecycleStage === "active") return "(none)";
  return formatValue(knob.valueType, knob.finalValue);
}

function parseTypedValue(raw: string, valueType: SerializedKnob["valueType"]): unknown {
  switch (valueType) {
    case "boolean":
      if (raw !== "true" && raw !== "false") {
        console.error('Error: Boolean value must be "true" or "false".');
        process.exit(1);
      }
      return raw === "true";
    case "string":
      return raw;
    case "number": {
      const num = parseFloat(raw);
      if (Number.isNaN(num)) {
        console.error(`Error: Invalid number: ${raw}`);
        process.exit(1);
      }
      return num;
    }
    case "json":
      try {
        return JSON.parse(raw);
      } catch {
        console.error(`Error: Invalid JSON: ${raw}`);
        process.exit(1);
      }
  }
}

function fetchKnobs(apiUrl: string, token: string): Promise<SerializedKnob[]> {
  return fetchList<SerializedKnob>(apiUrl, token, "/superadmin/knobs", "knobs");
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, "--json");
  const knobs = await fetchKnobs(apiUrl, token);

  if (jsonOutput) {
    console.log(JSON.stringify(knobs, null, 2));
    return;
  }

  if (knobs.length === 0) {
    console.log("No knobs found.");
    return;
  }

  for (const knob of knobs) {
    const desc = knob.description ? `  ${knob.description}` : "";
    const lifecycle = `lifecycle=${toLifecycleLabel(knob.lifecycleStage)}`;
    const finalValue =
      knob.lifecycleStage === "active" ? "" : ` final=${formatFinalValue(knob)}`;
    const stale = knob.isStale ? " [STALE]" : "";
    console.log(
      `  ${knob.key}  [${knob.valueType}] = ${formatValue(knob.valueType, knob.value)}  (${lifecycle}${finalValue})${desc}${stale}`
    );
  }
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const key = argv[0];
  if (!key || key.startsWith("--")) {
    console.error("Error: Missing knob key.");
    console.error("Usage: canary knobs get <key>");
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, "--json");
  const knobs = await fetchKnobs(apiUrl, token);
  const knob = knobs.find((k) => k.key === key);

  if (!knob) {
    console.error(`Knob not found: ${key}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(knob, null, 2));
    return;
  }

  console.log(`  Key:         ${knob.key}`);
  console.log(`  Type:        ${knob.valueType}`);
  console.log(`  Value:       ${formatValue(knob.valueType, knob.value)}`);
  console.log(`  Lifecycle:   ${toLifecycleLabel(knob.lifecycleStage)}`);
  console.log(`  Final value: ${formatFinalValue(knob)}`);
  console.log(`  Description: ${knob.description ?? "(none)"}`);
  console.log(`  Updated:     ${knob.updatedAt ?? "(never)"}`);
  if (knob.isStale) {
    console.log(`  Status:      STALE (no changes in 30+ days)`);
  }
}

async function handleSet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const key = argv[0];
  if (!key || key.startsWith("--")) {
    console.error("Error: Missing knob key.");
    console.error("Usage: canary knobs set <key> <value> --type <boolean|string|number|json> [--description <text>]");
    process.exit(1);
  }

  const rawValue = argv[1];
  if (rawValue === undefined || rawValue.startsWith("--")) {
    console.error("Error: Missing knob value.");
    console.error("Usage: canary knobs set <key> <value> --type <boolean|string|number|json> [--description <text>]");
    process.exit(1);
  }

  const valueType = getArgValue(argv, "--type") as SerializedKnob["valueType"] | undefined;
  if (!valueType || !["boolean", "string", "number", "json"].includes(valueType)) {
    console.error("Error: --type is required and must be one of: boolean, string, number, json");
    process.exit(1);
  }

  const value = parseTypedValue(rawValue, valueType);
  const description = getArgValue(argv, "--description") ?? undefined;

  const result = await apiRequest<KnobApiResponse>(apiUrl, token, "POST", "/superadmin/knobs", {
    key,
    valueType,
    value,
    description,
  });

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Set knob: ${key} = ${formatValue(valueType, result.knob?.value)}`);
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const key = argv[0];
  if (!key || key.startsWith("--")) {
    console.error("Error: Missing knob key.");
    console.error("Usage: canary knobs delete <key>");
    process.exit(1);
  }

  const result = await apiRequest<KnobApiResponse>(
    apiUrl, token, "DELETE", `/superadmin/knobs/${encodeURIComponent(key)}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Deleted knob: ${key}`);
}

async function handleToggle(argv: string[], apiUrl: string, token: string): Promise<void> {
  const key = argv[0];
  if (!key || key.startsWith("--")) {
    console.error("Error: Missing knob key.");
    console.error("Usage: canary knobs toggle <key>");
    process.exit(1);
  }

  const result = await apiRequest<KnobApiResponse>(
    apiUrl, token, "POST", `/superadmin/knobs/${encodeURIComponent(key)}/toggle`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  const knob = result.knob;
  console.log(`Toggled knob: ${key} -> ${formatValue(knob?.valueType ?? "boolean", knob?.value)}`);
}

async function handleLifecycle(argv: string[], apiUrl: string, token: string): Promise<void> {
  const key = argv[0];
  if (!key || key.startsWith("--")) {
    console.error("Error: Missing knob key.");
    console.error("Usage: canary knobs lifecycle <key> --stage <active|deprecated|ready_for_cleanup> [--final-value <value>]");
    process.exit(1);
  }

  const stage = parseLifecycleStage(argv, KNOB_LIFECYCLE_STAGES);
  const rawFinalValue = getArgValue(argv, "--final-value");
  const clearFinalValue = hasFlag(argv, "--clear-final-value");

  if (rawFinalValue !== undefined && clearFinalValue) {
    console.error("Error: use either --final-value or --clear-final-value, not both.");
    process.exit(1);
  }

  const knobs = await fetchKnobs(apiUrl, token);
  const knob = knobs.find((k) => k.key === key);

  if (!knob) {
    console.error(`Knob not found: ${key}`);
    process.exit(1);
  }

  let finalValue: unknown = undefined;

  if (stage === "active") {
    if (rawFinalValue !== undefined) {
      console.error("Error: active stage does not accept --final-value. Use --stage deprecated|ready_for_cleanup.");
      process.exit(1);
    }
    finalValue = clearFinalValue ? undefined : undefined;
  } else {
    if (clearFinalValue) {
      console.error("Error: --clear-final-value can only be used with --stage active.");
      process.exit(1);
    }
    if (rawFinalValue === undefined) {
      console.error("Error: --final-value is required when stage is deprecated or ready_for_cleanup.");
      process.exit(1);
    }
    finalValue = parseTypedValue(rawFinalValue, knob.valueType);
  }

  const result = await apiRequest<KnobApiResponse>(
    apiUrl, token, "POST", `/superadmin/knobs/${encodeURIComponent(key)}/lifecycle`,
    { stage, finalValue }
  );

  if (!result.ok || !result.knob) {
    console.error(`Error: ${result.error ?? "Failed to update lifecycle"}`);
    process.exit(1);
  }

  console.log(
    `Updated lifecycle for ${key}: stage=${toLifecycleLabel(result.knob.lifecycleStage)}, final=${formatFinalValue(result.knob)}`
  );
}

function printKnobsHelp(): void {
  console.log(
    [
      "Usage: canary knobs <sub-command> [options]",
      "",
      "Sub-commands:",
      "  list                                       List all knobs",
      "  get <key>                                  Get a knob value",
      "  set <key> <value> --type <type> [--description <text>]",
      "                                             Set a knob value",
      "  delete <key>                               Delete a knob",
      "  toggle <key>                               Toggle a boolean knob",
      "  lifecycle <key> --stage <stage> [--final-value <value>]",
      "                                             Mark knob lifecycle + final value",
      "",
      "Types: boolean, string, number, json",
      "Stages: active, deprecated, ready_for_cleanup",
      "",
      "Options:",
      "  --final-value <value>  Final value for deprecated/ready_for_cleanup",
      "  --clear-final-value    Clear final value (only valid with --stage active)",
      "  --env <env>            Target environment (prod, dev, local)",
      "  --json                 Output as JSON (list/get only)",
      "  --api-url <url>        API URL override (takes precedence over --env)",
      "  --token <key>          API token override",
    ].join("\n")
  );
}

export async function runKnobs(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === "help" || hasFlag(argv, "--help", "-h")) {
    printKnobsHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case "list":
      await handleList(rest, apiUrl, token);
      break;
    case "get":
      await handleGet(rest, apiUrl, token);
      break;
    case "set":
      await handleSet(rest, apiUrl, token);
      break;
    case "delete":
      await handleDelete(rest, apiUrl, token);
      break;
    case "toggle":
      await handleToggle(rest, apiUrl, token);
      break;
    case "lifecycle":
      await handleLifecycle(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printKnobsHelp();
      process.exit(1);
  }
}
